import { useState } from 'react';
import { makeStyles } from '@material-ui/styles';

import { Text, Pointer } from '@bizzabo/design-system';
import { UserPreview, UserData } from '@bizzabo/entities';

import MeetingItem from '../MeetingItem';
import { ScheduledMeeting } from '../../types';
import LocationDetails from '../LocationDetails';
import UserBadgeAction from '../UserBadgeAction';
import ItemDropdownMenu from '../ItemDropdownMenu';
import ItemDropdownMenuButton from '../ItemDropdownMenu/ItemDropdownMenuButton';
import DeclineInvitationModal from '../DeclineInvitationModal';
import useTypeTracking from '../../../../analytics';
import { isHost } from '../../helpers';

interface PendingItemProps {
    meeting: ScheduledMeeting;
    currentUser: UserData;
    user: UserPreview;
}

const AwaitingItem: React.FC<PendingItemProps> = ({ meeting, currentUser, user }) => {
    const classes = useStyles();
    const [isDeclineInvitationModalOpen, setIsDeclineInvitationModalOpen] = useState(false);
    const isCurrentUserHost = isHost(meeting, currentUser.userProfileId);

    const { Track, trackClickEvent } = useTypeTracking({
        data: {
            description: {
                meeting_id: meeting.id,
                location: 'MEETING_CARD',
                flow: 'REVIEW_PENDING',
                meeting_participant_role: isCurrentUserHost ? 'HOST' : 'GUEST',
                meeting_type: 'SCHEDULED',
            },
        },
    });

    const onCancelInvitation = () => {
        setIsDeclineInvitationModalOpen(true);
        trackClickEvent({
            subCategory: 'MEETING_CANCELLED_INITIATED',
        });
    };

    const closeDeclineInvitationModal = () => {
        setIsDeclineInvitationModalOpen(false);
        trackClickEvent({
            subCategory: 'MEETING_CANCELLED_CLOSED',
        });
    };

    return (
        <Track>
            <MeetingItem>
                <div className={classes.row}>
                    <Pointer />
                    <Text variant="t81" className={classes.text}>
                        Invitation sent
                    </Text>
                </div>
                <UserBadgeAction user={user} hasNotification={!!meeting.notifications?.length}>
                    <ItemDropdownMenu>
                        {(closeDropdown: () => void) => (
                            <ItemDropdownMenuButton
                                className={classes.redColor}
                                onClick={() => {
                                    closeDropdown();
                                    onCancelInvitation();
                                }}
                            >
                                Cancel invitation
                            </ItemDropdownMenuButton>
                        )}
                    </ItemDropdownMenu>
                </UserBadgeAction>
                <LocationDetails physicalLocation={meeting.physicalLocation} />
            </MeetingItem>

            <DeclineInvitationModal
                isOpen={isDeclineInvitationModalOpen}
                meeting={meeting}
                closeModal={closeDeclineInvitationModal}
            />
        </Track>
    );
};

const useStyles = makeStyles(
    theme => ({
        row: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: 10,
        },
        text: {
            marginLeft: 7,
        },
        redColor: {
            color: theme.palette.primary[500],
        },
    }),
    { classNamePrefix: 'meetings-awaiting-item' }
);

export default AwaitingItem;
